import { HTMLAttributes } from 'react'
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline'

export interface AISuggestion {
  id: string
  title: string
  description: string
  actions?: Array<{
    label: string
    onClick: () => void
    variant?: 'primary' | 'secondary'
  }>
}

export interface AISuggestionsPanelProps extends HTMLAttributes<HTMLDivElement> {
  suggestions: AISuggestion[]
  onDismiss?: (id: string) => void
  dismissible?: boolean
}

const AISuggestionsPanel = ({ 
  suggestions, 
  onDismiss,
  dismissible = true,
  className = '',
  ...props 
}: AISuggestionsPanelProps) => {
  if (!suggestions.length) return null
  
  return (
    <div className={`space-y-3 ${className}`} {...props}>
      {suggestions.map(suggestion => (
        <AISuggestionItem
          key={suggestion.id}
          suggestion={suggestion}
          onDismiss={dismissible && onDismiss ? () => onDismiss(suggestion.id) : undefined}
        />
      ))}
    </div>
  )
}

interface AISuggestionItemProps {
  suggestion: AISuggestion
  onDismiss?: () => void
}

const AISuggestionItem = ({ suggestion, onDismiss }: AISuggestionItemProps) => {
  return (
    <div
      className="
        relative
        bg-accent
        border-l-4 border-primary
        rounded-lg p-4
      "
    >
      {/* AI Icon */}
      <div className="flex items-start gap-3">
        <SparklesIcon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />

        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground mb-1">
            {suggestion.title}
          </h4>
          <p className="text-sm text-muted-foreground mb-3">
            {suggestion.description}
          </p>
          
          {/* Action Buttons */}
          {suggestion.actions && suggestion.actions.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {suggestion.actions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={action.onClick}
                  className={`
                    px-3 py-1.5 text-xs font-medium rounded-md
                    transition-colors duration-150
                    ${action.variant === 'primary'
                      ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                      : 'bg-card text-primary border border-border hover:bg-accent'
                    }
                  `}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Dismiss Button */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 text-primary hover:text-primary/80 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  )
}

export default AISuggestionsPanel

